import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ColDef, ICellRendererParams, ModuleRegistry } from 'ag-grid-community';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { ZfBox, ZfInput, ZfSelectOption, ZfTextButton, ZfTooltip } from '@zafire/react';
import {
  SafeDepositBoxSafeRoleOption,
  SafeDepositBoxSignatureAuthorityOption,
  SignatureAuthority,
} from 'pco-product-service-internal-consumer';
import { getPartnerTypeLabel } from '../utils/partnerDisplayUtils';
import {
  isEditableServiceRoleRow,
  SAFE_HOLDER_API_VALUE,
  SAFE_POWER_OF_ATTORNEY_API_VALUE,
  SafeDepositBoxServiceRoleDraft,
  SafeDepositBoxServiceRoleError,
} from '../utils/safeDepositBoxServiceRoleDraft';

import { signatureAuthorityLabel } from '../../../productDetails/blocks/serviceRoleColumns/safeDepositBoxServiceRoleColumns';

ModuleRegistry.registerModules([AllCommunityModule]);

const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 56;
const MAX_VISIBLE_ROWS = 4;

type RemoveCellProps = {
  data: SafeDepositBoxServiceRoleDraft;
  disabled: boolean;
  onRemove: (key: string) => void;
};

const RemoveCell: React.FC<RemoveCellProps> = ({ data, disabled, onRemove }) => {
  const { t } = useTranslation();
  const buttonWrapperRef = useRef<HTMLDivElement | null>(null);
  const showProtectedReason = !data.isRemovable;
  const partnerName =
    data.fullName ?? data.partnerNumber ?? t('pb.configureproductmodal.safedepositbox.grid.partner.fallback');

  return (
    <ZfBox style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <div ref={buttonWrapperRef}>
        <ZfTextButton
          hierarchy="secondary"
          size="extra-small"
          data-testid={`safe-role-remove-${data.partnerUid}`}
          aria-label={t('pb.configureproductmodal.safedepositbox.grid.remove.arialabel', { partnerName })}
          disabled={disabled || !data.isRemovable}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            onRemove(data.key);
          }}
        >
          {t('pb.configureproductmodal.safedepositbox.grid.remove.label')}
        </ZfTextButton>
      </div>
      {showProtectedReason && (
        <ZfTooltip
          text={
            data.safeRole === SAFE_HOLDER_API_VALUE
              ? t('pb.configureproductmodal.safedepositbox.remove.safeholder.protected.tooltip')
              : t('pb.configureproductmodal.safedepositbox.remove.protected.tooltip')
          }
          anchor={() => buttonWrapperRef.current as HTMLElement}
          side="top"
        />
      )}
    </ZfBox>
  );
};

type Props = {
  rows: SafeDepositBoxServiceRoleDraft[];
  safeRoleOptions: SafeDepositBoxSafeRoleOption[];
  signatureAuthorityOptions: SafeDepositBoxSignatureAuthorityOption[];
  disabled: boolean;
  serviceRoleErrors: ReadonlyMap<string, SafeDepositBoxServiceRoleError>;
  onChange: (key: string, changes: Partial<SafeDepositBoxServiceRoleDraft>) => void;
  onRemove: (key: string) => void;
};

const signatureAuthorityCellLabel = (
  data: SafeDepositBoxServiceRoleDraft | undefined,
  options: SafeDepositBoxSignatureAuthorityOption[],
) =>
  options.find((option) => option.value === data?.signatureAuthority)?.label ??
  signatureAuthorityLabel(data?.signatureAuthority) ??
  data?.signatureAuthority ??
  '-';

type SignatureAuthorityCellProps = {
  data: SafeDepositBoxServiceRoleDraft;
  options: SafeDepositBoxSignatureAuthorityOption[];
  disabled: boolean;
  hasError: boolean;
  onChange: (key: string, changes: Partial<SafeDepositBoxServiceRoleDraft>) => void;
};

const SignatureAuthorityCell: React.FC<SignatureAuthorityCellProps> = ({
  data,
  options,
  disabled,
  hasError,
  onChange,
}) => {
  const { t } = useTranslation();
  const value = data.signatureAuthority ?? '';

  return (
    <ZfInput
      data-testid={`signature-authority-select-${data.partnerUid}`}
      name={`signature-authority-${data.partnerUid}`}
      type="select"
      mandatory
      value={value}
      disabled={disabled}
      error={hasError}
      onSelectChange={(event: CustomEvent<string | string[]>) => {
        const selected = Array.isArray(event.detail) ? event.detail[0] : event.detail;
        onChange(data.key, { signatureAuthority: selected ? (selected as SignatureAuthority) : undefined });
      }}
    >
      <ZfSelectOption value="" isSelected={!value}>
        {t('pb.configureproductmodal.safedepositbox.grid.signatureauthority.placeholder')}
      </ZfSelectOption>
      {options.map((option) => (
        <ZfSelectOption key={option.value} value={option.value} isSelected={value === option.value}>
          {option.label}
        </ZfSelectOption>
      ))}
    </ZfInput>
  );
};

export const SafeDepositBoxServiceRolesGrid: React.FC<Props> = ({
  rows,
  safeRoleOptions,
  signatureAuthorityOptions,
  disabled,
  serviceRoleErrors,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const columnDefs = useMemo<ColDef<SafeDepositBoxServiceRoleDraft>[]>(
    () => [
      {
        headerName: t('pb.configureproductmodal.safedepositbox.grid.column.name'),
        field: 'fullName',
        flex: 1.5,
        minWidth: 150,
        filter: 'agTextColumnFilter',
      },
      {
        headerName: t('pb.configureproductmodal.safedepositbox.grid.column.id'),
        field: 'partnerNumber',
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        headerName: t('pb.configureproductmodal.safedepositbox.grid.column.type'),
        flex: 1,
        minWidth: 130,
        valueGetter: ({ data }) => getPartnerTypeLabel(data?.partnerType),
      },
      {
        headerName: t('pb.configureproductmodal.safedepositbox.grid.column.saferole'),
        flex: 1.5,
        minWidth: 190,
        cellRenderer: ({ data }: ICellRendererParams<SafeDepositBoxServiceRoleDraft>) => {
          if (!data) return null;
          if (data.safeRole === SAFE_HOLDER_API_VALUE) {
            return t('pb.configureproductmodal.safedepositbox.saferole.safeholder');
          }
          if (data.safeRole === SAFE_POWER_OF_ATTORNEY_API_VALUE) {
            return t('pb.configureproductmodal.safedepositbox.saferole.safepowerofattorney');
          }
          return safeRoleOptions.find((option) => option.value === data.safeRole)?.label ?? data.safeRole ?? '-';
        },
      },
      {
        headerName: t('pb.configureproductmodal.safedepositbox.grid.column.signatureauthority'),
        field: 'signatureAuthority',
        flex: 1.5,
        minWidth: 210,
        // The cell shows a label, so sorting on the raw enum would order COLLECTIVE_10 before COLLECTIVE_2.
        valueFormatter: ({ data }) => (data ? signatureAuthorityCellLabel(data, signatureAuthorityOptions) : ''),
        comparator: (_left, _right, leftNode, rightNode) =>
          signatureAuthorityCellLabel(leftNode.data, signatureAuthorityOptions).localeCompare(
            signatureAuthorityCellLabel(rightNode.data, signatureAuthorityOptions),
          ),
        cellRenderer: ({ data }: ICellRendererParams<SafeDepositBoxServiceRoleDraft>) => {
          if (!data) return null;
          if (!isEditableServiceRoleRow(data)) return signatureAuthorityCellLabel(data, signatureAuthorityOptions);

          return (
            <SignatureAuthorityCell
              data={data}
              options={signatureAuthorityOptions}
              disabled={disabled}
              hasError={serviceRoleErrors.get(data.key) === 'signatureAuthorityRequired'}
              onChange={onChange}
            />
          );
        },
      },
      {
        headerName: t('pb.configureproductmodal.safedepositbox.grid.column.actions'),
        type: 'rightAligned',
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        sortable: false,
        cellRenderer: ({ data }: ICellRendererParams<SafeDepositBoxServiceRoleDraft>) => {
          if (!data) return null;

          return <RemoveCell data={data} disabled={disabled} onRemove={onRemove} />;
        },
      },
    ],
    [disabled, onChange, onRemove, safeRoleOptions, serviceRoleErrors, signatureAuthorityOptions, t],
  );

  // Sized to the rows rather than to the viewport: on a short screen the dialog body scrolls instead
  // of squeezing the grid down to a single partner.
  const gridHeight = HEADER_HEIGHT + Math.min(Math.max(rows.length, 1), MAX_VISIBLE_ROWS) * ROW_HEIGHT;

  return (
    <div data-testid="safe-deposit-box-service-roles-grid">
      <div style={{ width: '100%', height: gridHeight }}>
        <AgGridReact<SafeDepositBoxServiceRoleDraft>
          rowData={rows}
          columnDefs={columnDefs}
          getRowId={({ data }) => data.key}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          suppressCellFocus
          suppressRowHoverHighlight
          theme={themeZafire}
        />
      </div>
    </div>
  );
};
